import { DynamicObject, TranslationOptions, TranslationsMetadata } from '../types';
import mercuryUtils from '../../badge/utils/mercuryUtils';
import { Nullable } from '../../types/common';
import TranslationHelper from '../../common/translationHelper/TranslationHelper';

type WindowWithReactSettings = { reactSettings?: { packageName?: string } };
/*
  The purpose of this class is to transform translations objects.
  It receives 2 translations objects: initial (might be in any locale) and en-GB one.
  If fullMetadataMode = true, instead of values of the translations, paths will be shown.
  If partialMetadataMode = true, instead of values that are not translated, enGB value will be shown with path and Epic number.
  Mode is determined by provided options. Refer to TranslationOptions type.
  Class instance is supposed to be singleton. Initialise it once, then use everywhere.

  For reference: https://reedexpo.atlassian.net/browse/MER-4778
 */
export class MercuryTranslationsUtils {
  private _fullMetadataMode = false;
  private _partialMetadataMode = false;

  get fullMetadataMode(): boolean {
    return this._fullMetadataMode;
  }

  get partialMetadataMode(): boolean {
    return this._partialMetadataMode;
  }

  set fullMetadataMode(value: boolean) {
    this._fullMetadataMode = value;
  }

  set partialMetadataMode(value: boolean) {
    this._partialMetadataMode = value;
  }

  parseJSON(data: DynamicObject, path: string, result: string[]) {
    for (const key in data) {
      const currentPath = path + key;
      if (typeof data[key] == 'object') {
        this.parseJSON(data[key], currentPath + '.', result);
      }
      result.push(currentPath);
    }
    return result;
  }

  getValueByPath(obj: DynamicObject, valuePath: string) {
    if (!valuePath.includes('.')) {
      return obj[valuePath];
    }

    for (let i = 0, path = valuePath.split('.'), len = path.length; i < len; i++) {
      obj = obj[path[i]];
    }
    return obj;
  }

  setToValue(obj: DynamicObject, value: string, path: string) {
    let i;
    let valuePath = path.split('.');
    for (i = 0; i < valuePath.length - 1; i++) {
      obj = obj[valuePath[i]];
    }

    obj[valuePath[i]] = value;
  }

  getEpicByPath(translationsMetadata: TranslationsMetadata, path: string) {
    return translationsMetadata.Translations.find((row) => row.Path === path)?.Epic;
  }

  searchAndHighlightMetadataElements(text: string) {
    const iterator = document.createNodeIterator(document.body, NodeFilter.SHOW_TEXT);
    let textNode: Nullable<CharacterData>;
    while ((textNode = iterator.nextNode() as CharacterData)) {
      if (textNode?.data.includes(text)) {
        textNode.parentElement!.style.setProperty('background-color', 'chartreuse', 'important');
      }
    }
  }

  /*
    Interval is needed to handle scenarios when the page content is changed.
    It will change the background of the elements if specific condition is met every X ms (value from options).
   */
  startSearchInterval = (delay: number) => {
    setInterval(() => {
      this.searchAndHighlightMetadataElements(TranslationHelper.appName);
    }, delay);
  };

  getPartialModeTranslation = (valueInCurrentLocale: string, valueInEnGb: string, path: string) =>
    valueInCurrentLocale === valueInEnGb
      ? `${valueInCurrentLocale} [${TranslationHelper.appName}.${path}]`
      : valueInCurrentLocale;

  getFullModeTranslation = (currentValue: string, path: string) => `${currentValue} [${TranslationHelper.appName}.${path}]`;

  /*
    Should be called from outside to determine mode BEFORE any translation file is modified.
   */
  setupMetadataMode(options: TranslationOptions) {
    const { urlParams } = options;
    if (urlParams) {
      const fullMetadataMode = mercuryUtils.getUrlParam(urlParams.fullMode, window.location.href) === 'true';
      if (fullMetadataMode) {
        return (this._fullMetadataMode = true);
      }
      const partialMetadataMode = mercuryUtils.getUrlParam(urlParams.partialMode, window.location.href) === 'true';
      if (partialMetadataMode) {
        return (this._partialMetadataMode = true);
      }
    }
    this._fullMetadataMode = Boolean(options.fullMetadataMode);
    this._partialMetadataMode = Boolean(options.partialMetadataMode);
  }

  getMappedTranslations(initialTranslations: DynamicObject, enGbTranslations: DynamicObject) {
    const pathsForCurrentLocale = this.parseJSON(initialTranslations, '', []);
    const resultTranslations: DynamicObject = JSON.parse(JSON.stringify(initialTranslations));

    pathsForCurrentLocale.forEach((path) => {
      const valueInCurrentLocale = this.getValueByPath(initialTranslations, path);
      const valueInEnGb = this.getValueByPath(enGbTranslations, path);
      if (typeof valueInCurrentLocale !== 'object') {
        let valueToSet = '';
        if (this._fullMetadataMode) {
          valueToSet = this.getFullModeTranslation(valueInCurrentLocale, path);
        } else if (this._partialMetadataMode) {
          valueToSet = this.getPartialModeTranslation(valueInCurrentLocale, valueInEnGb, path);
        }
        this.setToValue(resultTranslations, valueToSet, path);
      }
    });
    return resultTranslations;
  }
}

export default MercuryTranslationsUtils;
